import Decimal from 'decimal.js-light';

export type MoneyRounding =
  | 'halfUp'
  | 'halfEven'
  | 'truncate'
  | 'ceil'
  | 'floor'
  | 'awayFromZero';

export type PrettyMoneyOptions = {
  fallback?: number | string;
  rounding?: MoneyRounding;
  decimalPlaces?: number;
};

export const CURRENCY = '€';

const DEFAULT_DECIMAL_PLACES = 2;
const DEFAULT_ROUNDING: MoneyRounding = 'truncate';
const CURRENCY_SEPARATOR = '\u202F';
const THOUSANDS_SEPARATOR = "'";
const DECIMAL_SEPARATOR = '.';
const FALLBACK = '—';

const DECIMAL_ROUNDING_MAP: Record<MoneyRounding, number> = {
  halfUp: Decimal.ROUND_HALF_UP,
  halfEven: Decimal.ROUND_HALF_EVEN,
  truncate: Decimal.ROUND_DOWN,
  ceil: Decimal.ROUND_CEIL,
  floor: Decimal.ROUND_FLOOR,
  awayFromZero: Decimal.ROUND_UP,
};

export function roundMoney(
  amount: number,
  decimalPlaces: number = DEFAULT_DECIMAL_PLACES,
  rounding: MoneyRounding = DEFAULT_ROUNDING,
): number {
  if (!Number.isFinite(amount)) {
    throw new TypeError('amount must be a finite number');
  }

  if (!Number.isInteger(decimalPlaces) || decimalPlaces < 0) {
    throw new RangeError('decimalPlaces must be an integer greater than or equal to 0');
  }

  const decimalRounding = DECIMAL_ROUNDING_MAP[rounding];

  if (decimalRounding === undefined) {
    throw new TypeError(`Unsupported rounding mode: ${rounding}`);
  }

  const rounded = new Decimal(amount).toDecimalPlaces(decimalPlaces, decimalRounding).toNumber();

  return Object.is(rounded, -0) ? 0 : rounded;
}

export function prettyMoney(
  amount: number | null | undefined,
  options: PrettyMoneyOptions = {},
): string {
  const {
    fallback = FALLBACK,
    rounding = DEFAULT_ROUNDING,
    decimalPlaces = DEFAULT_DECIMAL_PLACES,
  } = options;

  if (typeof amount !== 'number' || Number.isNaN(amount) || !Number.isFinite(amount)) {
    return String(fallback);
  }

  const roundedAmount = roundMoney(amount, decimalPlaces, rounding);
  const sign = roundedAmount < 0 ? '-' : '';
  const absoluteAmount = Math.abs(roundedAmount);
  const value = absoluteAmount.toFixed(decimalPlaces);
  const [integerPart = '0', fractionPart] = value.split(DECIMAL_SEPARATOR);
  const formattedIntegerPart = integerPart.replace(
    /\B(?=(\d{3})+(?!\d))/g,
    THOUSANDS_SEPARATOR,
  );

  if (fractionPart === undefined) {
    return `${sign}${CURRENCY}${CURRENCY_SEPARATOR}${formattedIntegerPart}`;
  }

  return `${sign}${CURRENCY}${CURRENCY_SEPARATOR}${formattedIntegerPart}${DECIMAL_SEPARATOR}${fractionPart}`;
}
